import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Trash2 } from 'lucide-react';
import { PersonAvatar } from '@/shared/components/PersonAvatar';
import type { Subscriber } from '../api/subscribersApi';
import { tint } from './styles';

const DATE = new Intl.DateTimeFormat('en-SG', { day: 'numeric', month: 'short', year: 'numeric' });

function OptIn({ s }: { s: Subscriber }) {
  const on = s.optedIn;
  return (
    <Box component="span" sx={{ ...pillSx, bgcolor: tint(on ? 'success-main' : 'warning-main', 12), color: on ? 'success.dark' : 'warning.main' }}>
      {on ? `Opted in${s.optedInAt ? ` ${DATE.format(new Date(s.optedInAt))}` : ''}` : 'Not opted in · receives nothing'}
    </Box>
  );
}

type Props = { s: Subscriber; canEdit: boolean; confirming: boolean; removing: boolean; onAsk: () => void; onKeep: () => void; onRemove: () => void };

/** One person on the list: name, masked number, consent; removing asks once, in place. */
export function MemberRow({ s, canEdit, confirming, removing, onAsk, onKeep, onRemove }: Props) {
  return (
    <Stack component="li" direction="row" sx={{ ...rowSx, bgcolor: confirming ? tint('error-main', 7) : 'transparent' }}>
      <PersonAvatar name={s.name} size={34} />
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>{s.name}</Typography>
        <Typography variant="caption" noWrap component="p" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
          {s.phoneMasked}{s.emailMasked ? ` · ${s.emailMasked}` : ''}
        </Typography>
      </Box>
      {confirming ? (
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}>Remove from this list?</Typography>
          <Button size="small" onClick={onKeep} sx={{ color: 'text.primary' }}>Keep</Button>
          <Button size="small" variant="contained" color="error" disabled={removing} onClick={onRemove}>Remove</Button>
        </Stack>
      ) : (
        <>
          <OptIn s={s} />
          {canEdit ? (
            <IconButton size="small" aria-label={`Remove ${s.name}`} onClick={onAsk} sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
              <Trash2 size={16} />
            </IconButton>
          ) : null}
        </>
      )}
    </Stack>
  );
}

const rowSx = {
  alignItems: 'center',
  gap: 1.5,
  px: { xs: 2, md: 2.5 },
  py: 1.25,
  borderBottom: 1,
  borderColor: 'divider',
  transition: 'background-color 150ms',
} as const;

const pillSx = {
  display: { xs: 'none', sm: 'inline-flex' },
  alignItems: 'center',
  gap: 0.5,
  px: 1,
  py: 0.25,
  borderRadius: 5,
  fontSize: '0.6875rem',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  flexShrink: 0,
  '&::before': { content: '""', width: 5, height: 5, borderRadius: '50%', bgcolor: 'currentColor' },
} as const;
